import React, {
  FormEvent,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import {
  FontAwesomeIcon,
} from "@fortawesome/react-fontawesome";

import {
  faArrowRight,
  faCheck,
  faEye,
  faEyeSlash,
  faSave,
  faShieldHalved,
  faUserPlus,
} from "@fortawesome/free-solid-svg-icons";

import toast from "react-hot-toast";

import {
  apiClient,
} from "../api/apiClient";

import {
  dashboardUsersApi,
} from "../api/dashboardUsersApi";

import "../screenstyles/DashboardUserForm.css";

type RoleItem = {
  roleId: string;
  name: string;
  nameAr?: string | null;
  description?: string | null;
  permissionIds: string[];
  isActive: boolean;
};

type PermissionItem = {
  permissionId: string;
};

type RolesResponse = {
  items?: RoleItem[];
  roles?: RoleItem[];
};

type PermissionsResponse = {
  items?: PermissionItem[];
  permissions?: PermissionItem[];
};

const AddDashboardUser:
React.FC = () => {
  const navigate =
    useNavigate();

  const [
    loading,
    setLoading,
  ] =
    useState(false);

  const [
    loadingOptions,
    setLoadingOptions,
  ] =
    useState(true);

  const [
    showPassword,
    setShowPassword,
  ] =
    useState(false);

  const [
    roles,
    setRoles,
  ] =
    useState<RoleItem[]>(
      [],
    );

  const [
    permissions,
    setPermissions,
  ] =
    useState<
      PermissionItem[]
    >([]);

  const [
    form,
    setForm,
  ] =
    useState({
      fullName: "",
      displayName: "",
      email: "",
      phoneNumber: "",
      password: "",
      roleId: "",
      permissionIds:
        [] as string[],
      isActive: true,
      isSuperAdmin: false,
    });

  useEffect(() => {
    const loadOptions =
      async () => {
        setLoadingOptions(
          true,
        );

        try {
          const [
            rolesData,
            permissionsData,
          ] =
            await Promise.all([
              apiClient.get<
                RolesResponse
              >(
                "/roles",
              ),

              apiClient.get<
                PermissionsResponse
              >(
                "/roles/permissions",
              ),
            ]);

          const roleItems =
            rolesData.items ??
            rolesData.roles ??
            [];

          const permissionItems =
            permissionsData.items ??
            permissionsData.permissions ??
            [];

          setRoles(
            roleItems.filter(
              role =>
                role.isActive,
            ),
          );

          setPermissions(
            permissionItems,
          );
        } catch (
          error
        ) {
          console.error(
            "Failed to load roles:",
            error,
          );

          toast.error(
            "فشل تحميل الأدوار والصلاحيات",
          );
        } finally {
          setLoadingOptions(
            false,
          );
        }
      };

    loadOptions();
  }, []);

  const selectedRole =
    useMemo(
      () =>
        roles.find(
          role =>
            role.roleId ===
            form.roleId,
        ) ?? null,
      [
        roles,
        form.roleId,
      ],
    );

  const togglePermission =
    (
      permissionId:
        string,
    ) => {
      setForm(prev => {
        const exists =
          prev.permissionIds.includes(
            permissionId,
          );

        return {
          ...prev,

          permissionIds:
            exists
              ? prev.permissionIds.filter(
                  id =>
                    id !==
                    permissionId,
                )
              : [
                  ...prev.permissionIds,
                  permissionId,
                ],
        };
      });
    };

  const handleSubmit =
    async (
      event:
        FormEvent,
    ) => {
      event.preventDefault();

      if (
        !form.fullName.trim()
      ) {
        toast.error(
          "أدخل الاسم الكامل",
        );
        return;
      }

      if (
        !form.email.trim()
      ) {
        toast.error(
          "أدخل البريد الإلكتروني",
        );
        return;
      }

      if (
        form.password.length <
        8
      ) {
        toast.error(
          "كلمة المرور يجب أن تكون 8 أحرف على الأقل",
        );
        return;
      }

      setLoading(true);

      try {
        await dashboardUsersApi
          .create({
            fullName:
              form.fullName.trim(),

            displayName:
              form.displayName.trim() ||
              null,

            email:
              form.email.trim(),

            password:
              form.password,

            phoneNumber:
              form.phoneNumber.trim() ||
              null,

            roleId:
              form.roleId ||
              null,

            permissionIds:
              form.permissionIds,

            isActive:
              form.isActive,

            isSuperAdmin:
              form.isSuperAdmin,
          });

        toast.success(
          "تم إنشاء مستخدم الداش بورد بنجاح",
        );

        navigate(
          "/dashboard-users",
        );
      } catch (
        error: any
      ) {
        console.error(
          "Failed to create dashboard user:",
          error,
        );

        toast.error(
          error?.message ||
            "فشل إنشاء المستخدم",
        );
      } finally {
        setLoading(false);
      }
    };

  return (
    <div className="dashboard-user-form-page">
      <div className="dashboard-user-form-header">
        <div>
          <span className="dashboard-user-form-eyebrow">
            إدارة مستخدمي الداش بورد
          </span>

          <h1>
            إضافة مستخدم جديد
          </h1>

          <p>
            إنشاء حساب Firebase Auth وربطه
            بدور وصلاحيات داخل لوحة التحكم.
          </p>
        </div>

        <button
          type="button"
          className="dashboard-user-form-back"
          onClick={() =>
            navigate(
              "/dashboard-users",
            )
          }
        >
          <FontAwesomeIcon
            icon={
              faArrowRight
            }
          />

          رجوع
        </button>
      </div>

      <form
        className="dashboard-user-form-card"
        onSubmit={
          handleSubmit
        }
      >
        <div className="dashboard-user-form-section">
          <div className="dashboard-user-form-section-title">
            <FontAwesomeIcon
              icon={
                faUserPlus
              }
            />

            <div>
              <h2>
                البيانات الأساسية
              </h2>

              <p>
                معلومات حساب الموظف.
              </p>
            </div>
          </div>

          <div className="dashboard-user-form-grid">
            <div className="dashboard-user-field">
              <label>
                الاسم الكامل *
              </label>

              <input
                type="text"
                value={
                  form.fullName
                }
                onChange={event =>
                  setForm(
                    prev => ({
                      ...prev,
                      fullName:
                        event
                          .target
                          .value,
                    }),
                  )
                }
                placeholder="مثال: أحمد محمد"
              />
            </div>

            <div className="dashboard-user-field">
              <label>
                الاسم الظاهر
              </label>

              <input
                type="text"
                value={
                  form.displayName
                }
                onChange={event =>
                  setForm(
                    prev => ({
                      ...prev,
                      displayName:
                        event
                          .target
                          .value,
                    }),
                  )
                }
                placeholder="مثال: أحمد"
              />
            </div>

            <div className="dashboard-user-field">
              <label>
                البريد الإلكتروني *
              </label>

              <input
                type="email"
                value={
                  form.email
                }
                onChange={event =>
                  setForm(
                    prev => ({
                      ...prev,
                      email:
                        event
                          .target
                          .value,
                    }),
                  )
                }
                placeholder="employee@shoof.app"
              />
            </div>

            <div className="dashboard-user-field">
              <label>
                رقم الهاتف
              </label>

              <input
                type="text"
                value={
                  form.phoneNumber
                }
                onChange={event =>
                  setForm(
                    prev => ({
                      ...prev,
                      phoneNumber:
                        event
                          .target
                          .value,
                    }),
                  )
                }
                placeholder="+9647700000000"
                dir="ltr"
              />
            </div>

            <div className="dashboard-user-field dashboard-user-field-full">
              <label>
                كلمة المرور *
              </label>

              <div className="dashboard-user-password-input">
                <input
                  type={
                    showPassword
                      ? "text"
                      : "password"
                  }
                  value={
                    form.password
                  }
                  onChange={event =>
                    setForm(
                      prev => ({
                        ...prev,
                        password:
                          event
                            .target
                            .value,
                      }),
                    )
                  }
                  placeholder="8 أحرف على الأقل"
                />

                <button
                  type="button"
                  onClick={() =>
                    setShowPassword(
                      prev =>
                        !prev,
                    )
                  }
                >
                  <FontAwesomeIcon
                    icon={
                      showPassword
                        ? faEyeSlash
                        : faEye
                    }
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-user-form-section">
          <div className="dashboard-user-form-section-title">
            <FontAwesomeIcon
              icon={
                faShieldHalved
              }
            />

            <div>
              <h2>
                الدور والصلاحيات
              </h2>

              <p>
                اختر الدور ثم أضف أي صلاحيات
                مباشرة إضافية.
              </p>
            </div>
          </div>

          <div className="dashboard-user-field">
            <label>
              الدور
            </label>

            <select
              disabled={
                loadingOptions
              }
              value={
                form.roleId
              }
              onChange={event =>
                setForm(
                  prev => ({
                    ...prev,
                    roleId:
                      event
                        .target
                        .value,
                  }),
                )
              }
            >
              <option value="">
                بدون دور
              </option>

              {roles.map(
                role => (
                  <option
                    key={
                      role.roleId
                    }
                    value={
                      role.roleId
                    }
                  >
                    {role.nameAr ||
                      role.name}
                  </option>
                ),
              )}
            </select>
          </div>

          {selectedRole && (
            <div className="dashboard-user-role-summary">
              <strong>
                صلاحيات الدور:
              </strong>

              <span>
                {
                  selectedRole
                    .permissionIds
                    .length
                }{" "}
                صلاحية
              </span>
            </div>
          )}

          <div className="dashboard-user-permissions">
            <div className="dashboard-user-permissions-header">
              <strong>
                الصلاحيات المباشرة
              </strong>

              <span>
                {
                  form
                    .permissionIds
                    .length
                }{" "}
                محددة
              </span>
            </div>

            {loadingOptions ? (
              <div className="dashboard-user-options-loading">
                جاري تحميل الصلاحيات...
              </div>
            ) : (
              <div className="dashboard-user-permission-grid">
                {permissions.map(
                  permission => {
                   const checked =
  form.permissionIds.includes(
    permission.permissionId,
  );

                    return (
                      <button
                        type="button"
                       key={
  permission.permissionId
}
                        className={
                          checked
                            ? "dashboard-user-permission-card selected"
                            : "dashboard-user-permission-card"
                        }
                       onClick={() =>
  togglePermission(
    permission.permissionId,
  )
}
                      >
                        <span className="dashboard-user-permission-check">
                          {checked && (
                            <FontAwesomeIcon
                              icon={
                                faCheck
                              }
                            />
                          )}
                        </span>

                        <div>
                         <strong>
  {permission.permissionId}
</strong>

<small>
  {permission.permissionId}
</small>

                        
                        </div>
                      </button>
                    );
                  },
                )}
              </div>
            )}
          </div>
        </div>

        <div className="dashboard-user-form-section">
          <h2>
            إعدادات الحساب
          </h2>

          <label className="dashboard-user-switch-row">
            <div>
              <strong>
                الحساب نشط
              </strong>

              <span>
                يمكن للمستخدم تسجيل الدخول
                والوصول للوحة التحكم.
              </span>
            </div>

            <input
              type="checkbox"
              checked={
                form.isActive
              }
              onChange={event =>
                setForm(
                  prev => ({
                    ...prev,
                    isActive:
                      event
                        .target
                        .checked,
                  }),
                )
              }
            />
          </label>

          <label className="dashboard-user-switch-row dashboard-user-super-admin-row">
            <div>
              <strong>
                Super Admin
              </strong>

              <span>
                صلاحية إدارية حساسة جدًا.
              </span>
            </div>

            <input
              type="checkbox"
              checked={
                form.isSuperAdmin
              }
              onChange={event =>
                setForm(
                  prev => ({
                    ...prev,
                    isSuperAdmin:
                      event
                        .target
                        .checked,
                  }),
                )
              }
            />
          </label>
        </div>

        <div className="dashboard-user-form-actions">
          <button
            type="button"
            className="dashboard-user-secondary-button"
            onClick={() =>
              navigate(
                "/dashboard-users",
              )
            }
          >
            إلغاء
          </button>

          <button
            type="submit"
            className="dashboard-user-primary-button"
            disabled={
              loading
            }
          >
            <FontAwesomeIcon
              icon={
                faSave
              }
            />

            {loading
              ? "جاري الإنشاء..."
              : "إنشاء المستخدم"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddDashboardUser;